/**
 * Document Viewer Modal Component
 * In-app preview for PDFs and images without requiring download
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Download, X, ZoomIn, ZoomOut, RotateCw, ExternalLink, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DocumentViewerModal = ({ 
  isOpen, 
  onClose, 
  documentUrl, 
  documentName,
  documentType 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  // Determine if it's a PDF or image based on extension or type
  const isPdf = documentName?.toLowerCase().endsWith('.pdf') || documentType === 'application/pdf';
  const isImage = !isPdf && (
    documentName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/) ||
    documentType?.startsWith('image/')
  );

  // Construct full URL
  const fullUrl = documentUrl?.startsWith('http') 
    ? documentUrl 
    : `${BACKEND_URL}${documentUrl}`;

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      setZoom(100);
      setRotation(0);
      
      // For non-previewable file types, disable loading spinner immediately
      if (!isPdf && !isImage) {
        setLoading(false);
      }
    }
  }, [isOpen, documentUrl, isPdf, isImage]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = documentName || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(fullUrl, '_blank');
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load document. Try downloading instead.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base font-medium">
              {isPdf ? (
                <FileText className="w-5 h-5 text-red-500" />
              ) : (
                <ImageIcon className="w-5 h-5 text-blue-500" />
              )}
              <span className="truncate max-w-[300px]" title={documentName}>
                {documentName || 'Document'}
              </span>
            </DialogTitle>
            
            {/* Toolbar */}
            <div className="flex items-center gap-1">
              {isImage && (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleZoomOut}
                    disabled={zoom <= 50}
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-slate-500 w-12 text-center">{zoom}%</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleZoomIn}
                    disabled={zoom >= 200}
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRotate}
                    title="Rotate"
                  >
                    <RotateCw className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-6 bg-slate-200 mx-1" />
                </>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleOpenInNewTab}
                title="Open in New Tab"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDownload}
                title="Download"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClose}
                title="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-slate-100 relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="text-sm text-slate-500">Loading document...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="flex flex-col items-center gap-3 text-center p-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-sm text-slate-600">{error}</p>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            </div>
          )}

          {isPdf ? (
            <iframe
              src={`${fullUrl}#toolbar=1&navpanes=0`}
              className="w-full h-full min-h-[500px] border-0"
              title={documentName || 'PDF Document'}
              onLoad={handleLoad}
              onError={handleError}
              style={{ display: loading ? 'none' : 'block' }}
            />
          ) : isImage ? (
            <div 
              className="w-full h-full flex items-center justify-center p-4 overflow-auto"
              style={{ display: loading && !error ? 'none' : 'flex' }}
            >
              <img
                src={fullUrl}
                alt={documentName || 'Document'}
                className="max-w-full transition-transform duration-200"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center'
                }}
                onLoad={handleLoad}
                onError={handleError}
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="flex flex-col items-center gap-3 text-center p-4">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-500" />
                </div>
                <p className="text-sm text-slate-600">
                  Preview not available for this file type
                </p>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerModal;
